import {
  TGroupConfig,
  TLayoutConfig,
} from 'react-lib/components/organisms/CustomFieldGroupsSettings';
import {
  TPointFieldGroup,
  TPointFieldIdsEntry,
  TPointFieldLayoutEntry,
  TPointFieldRowEntry,
} from 'react-lib/components/organisms/PointFields';
import { ECustomFieldType } from '../../custom-fields/custom-field-types-enums';
import {
  TCustomField,
  TCustomFieldList,
  TWorkspaceCustomFields,
} from '../../custom-fields/custom-fields.model';
import { TPointCustomField, TPointCustomFieldSubfield } from '@project/view-models';
import { TPointCustomFields } from '../points.model';

export type TCustomFieldPermissions = {
  [customFieldId: string]: { read: boolean; edit: boolean };
};

type TBuildPointFieldLayoutOptions = {
  fieldsLayout: TLayoutConfig[];
  workspaceCustomFields: TWorkspaceCustomFields;
  pointCustomFields: TPointCustomFields;
  canEditPoint: boolean;
  customFieldPermissions: TCustomFieldPermissions;
  onSave: (fieldId: string, value: string, mentionIds: string[]) => void;
  onSaveIds: (fieldId: string, selectedItemIds: string[]) => void;
  onSaveSubValues: (fieldId: string, subValues: TPointCustomFieldSubfield[]) => void;
};

type TToEntryOptions = Omit<TBuildPointFieldLayoutOptions, 'fieldsLayout'>;

const hasValue = <T>(entry: T | null): entry is T => entry !== null;

const getLeafIds = (items: TCustomFieldList[]): string[] =>
  items.flatMap((item) => (item.subList?.length ? getLeafIds(item.subList) : [item.id]));

const isFullySelected = (item: TCustomFieldList, selectedIds: string[]): boolean =>
  getLeafIds([item]).every((leafId) => selectedIds.includes(leafId));

const toLeafSelection = (items: TCustomFieldList[], selectedIds: string[]): string[] =>
  items.flatMap((item) => {
    if (selectedIds.includes(item.id)) {
      return getLeafIds([item]);
    }

    return item.subList?.length ? toLeafSelection(item.subList, selectedIds) : [];
  });

const toTopLevelSelection = (items: TCustomFieldList[], selectedIds: string[]): string[] =>
  items.flatMap((item) => {
    if (isFullySelected(item, selectedIds)) {
      return [item.id];
    }

    return item.subList?.length ? toTopLevelSelection(item.subList, selectedIds) : [];
  });
const toFieldValue = (
  field: TCustomField,
  pointCustomField: TPointCustomField | undefined,
): string => {
  if (field.type === ECustomFieldType.LIST) {
    return String(pointCustomField?.idOfChosenElement ?? '');
  }

  return pointCustomField?.value ?? '';
};

const canEditField = (
  field: TCustomField,
  { canEditPoint, customFieldPermissions }: TToEntryOptions,
): boolean => {
  if (field.lockedValue) {
    return false;
  }

  return customFieldPermissions[field.id]?.edit ?? canEditPoint;
};

const toMultiListEntry = (
  fieldId: string,
  field: TCustomField,
  entryOptions: TToEntryOptions,
): TPointFieldIdsEntry => {
  const { pointCustomFields, onSaveIds } = entryOptions;
  const items = field.subList ?? [];

  return {
    field,
    selectedIds: toLeafSelection(items, pointCustomFields[fieldId]?.selectedItemIds ?? []),
    canEdit: canEditField(field, entryOptions),
    onSave: (selectedIds: string[]) => onSaveIds(fieldId, toTopLevelSelection(items, selectedIds)),
  };
};

const toEntry = (fieldId: string, entryOptions: TToEntryOptions): TPointFieldRowEntry | null => {
  const { workspaceCustomFields, pointCustomFields, onSave, onSaveSubValues } = entryOptions;
  const field = workspaceCustomFields[fieldId];

  if (!field?.display) {
    return null;
  }

  if (field.type === ECustomFieldType.MULTI_LIST) {
    return toMultiListEntry(fieldId, field, entryOptions);
  }

  return {
    field,
    value: toFieldValue(field, pointCustomFields[fieldId]),
    subValues: pointCustomFields[fieldId]?.subValues ?? [],
    canEdit: canEditField(field, entryOptions),
    onSave: (value: string, mentionIds: string[] = []) => onSave(fieldId, value, mentionIds),
    onSaveSubValues: (subValues: TPointCustomFieldSubfield[]) =>
      onSaveSubValues(fieldId, subValues),
  };
};

const toGroup = (group: TGroupConfig, entryOptions: TToEntryOptions): TPointFieldGroup | null => {
  const fields = group.items.map((item) => toEntry(item.id, entryOptions)).filter(hasValue);

  if (fields.length === 0) {
    return null;
  }

  return { id: group.id ?? group.title, title: group.title, fields };
};

export const buildPointFieldLayout = ({
  fieldsLayout,
  ...entryOptions
}: TBuildPointFieldLayoutOptions): TPointFieldLayoutEntry[] =>
  fieldsLayout
    .map((layout) =>
      layout.type === 'GROUP' ? toGroup(layout, entryOptions) : toEntry(layout.id, entryOptions),
    )
    .filter(hasValue);
